import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import {
  createSearchState,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  formListValueFormatter,
  FormSearchResultAdvanced,
  objectToCSVByKey,
  useDataProvider,
  useLocalization,
  useSnackbar,
  useStore,
  useUserAccess,
} from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import FmdBadIcon from '@mui/icons-material/FmdBad';
import {
  IconLibrary,
  StackablePageButtons,
  FormFields,
  PageSection,
  getActiveInactiveDeleteStatus,
  getDefaultValueByControl,
  ArrowTabGroup,
  ArrowTab,
} from '@product-framework/index';
import {
  FORM_VIEW,
  LOCATIONS_FORM_VIEW,
} from '@product-framework/pages/ServiceProvider/constants';
import { useNavigate, useParams } from 'react-router-dom';
import { ServiceProviderLocationNumberSearchDisplay } from '@product-framework/components/FormFields';
import {
  id,
  serviceProviderId,
  createdByUserId,
  lastModifiedByUserId,
  locationName,
  locationNumber,
  isDefaultLocation,
  status,
} from './accessorFunctions';
import listSchema from './list.json';
import searchSchema from './search.json';
import validationSchema from './validations';
import ServiceProviderLocationEdit from '../edit';
import ServiceProviderLocationCreate from '../create';
import { useServiceProviderLocation } from '../components/ServiceProviderLocationProvider';
import ServiceProviderSkills from '../../Skills/index';
import SystemSettings from '../../SystemSettings/index';
import Performance from '../../Performance/index';
import Setup from '../../SetUp/index';

function ServiceProviderLocationList({
  serviceProviderId: pageId,
  tabErrors,
  onChange = null,
  action,
  entityId,
  currentIndex,
}) {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const store = useStore();
  const snackbar = useSnackbar();
  const [resultsFound, setResultsFound] = useState(0);
  const [stackablePageButtons, setStackablePageButtons] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const { hasInvalidLocationAddress = [] } = tabErrors;
  const spLocationContext = useServiceProviderLocation() || {};
  const userAccess = useUserAccess();

  const { subTabNumber } = useParams();

  const validTab = [
    LOCATIONS_FORM_VIEW.DETAILS,
    LOCATIONS_FORM_VIEW.SKILLS,
    LOCATIONS_FORM_VIEW.SETUP,
    LOCATIONS_FORM_VIEW.SYSTEM_SETTINGS,
    LOCATIONS_FORM_VIEW.PERFORMANCE,
  ].includes(parseInt(subTabNumber, 10));

  const tabValue = parseInt(validTab ? subTabNumber : 1, 10);

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);

  const listApiUrl = `${dataProvider.getApiUrl()}/sxcl/v1/service-providers/${pageId}/locations`;

  const createButton = {
    label: formatMessage({
      id: 'CREATE_NEW_SERVICEPROVIDERLOCATION_STACKABLE_BUTTON',
      defaultMessage: 'Create Service Provider Location',
    }),
    permissionName: 'CAN_CREATE_SP_LOCATIONS',
    Icon: IconLibrary.ServiceProviderLocation,
    variant: 'outlined',
    onClick: () => {
      navigate(
        `/serviceproviders/edit/${pageId}/${currentIndex + 1}/${
          FORM_VIEW.CREATE
        }`,
      );
    },
  };

  const listButton = {
    label: formatMessage({
      id: 'SHOW_SERVICEPROVIDERLOCATION_LIST_STACKABLE_BUTTON',
      defaultMessage: 'List Service Provider Locations',
    }),
    permissionName: 'CAN_LIST_SP_LOCATIONS',
    Icon: IconLibrary.ServiceProviderLocation,
    variant: 'outlined',
    onClick: () => {
      navigate(
        `/serviceproviders/edit/${pageId}/${currentIndex + 1}/${
          FORM_VIEW.LIST
        }`,
      );
    },
  };

  const searchButton = {
    label: !showSearch
      ? formatMessage({
          id: 'SHOW_SEARCH_STACKABLE_BUTTON',
          defaultMessage: 'Search',
        })
      : formatMessage({
          id: 'HIDE_SEARCH_STACKABLE_BUTTON',
          defaultMessage: 'Hide Search',
        }),
    Icon: !showSearch ? IconLibrary.Search : IconLibrary.HideSearch,
    variant: 'contained',
    permissionName: 'CAN_SEARCH_SP_LOCATIONS',
    onClick: () => {
      setShowSearch(!showSearch);
    },
  };

  const issueButton = {
    label: formatMessage({
      id: 'ISSUE_STACKABLE_BUTTON',
      defaultMessage: `${hasInvalidLocationAddress?.length} issue${
        hasInvalidLocationAddress?.length > 1 ? 's' : ''
      }`,
    }),
    Icon: FmdBadIcon,
    color: 'error',
    variant: 'contained',
    permissionName: 'CAN_SEARCH_SP_LOCATIONS',
    onClick: () => {
      const locationNumberString = objectToCSVByKey(
        hasInvalidLocationAddress,
        'locationNumber',
      );
      const filters = [
        {
          field: 'locationNumber',
          operator: 'in',
          value: locationNumberString,
        },
      ];
      // ServiceProviderLocationList is store name from locations list page
      store.domain.list.set('ServiceProviderLocationList', { filters });
      navigate(
        `/serviceproviders/edit/${pageId}/${currentIndex + 1}/${
          FORM_VIEW.LIST
        }`,
      );
    },
  };

  const preProcessSearchRequestData = data => {
    const { id: idArg, locationNumber: locationNumberArg, ...other } = data;

    const formattedLocationNumber = Array.isArray(locationNumberArg)
      ? objectToCSVByKey(locationNumberArg, 'value')
      : null;

    return Object.fromEntries(
      Object.entries({
        ...(formattedLocationNumber && {
          locationNumber: formattedLocationNumber,
        }),
        ...other,
      }).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );
  };

  const submitSearchForm = async data => {
    const requestData = preProcessSearchRequestData(data);
    const searchState = createSearchState(requestData, searchSchema);
    store.domain.list.set('ServiceProviderLocationList', searchState);
  };

  const preProcessSearchValues = async searchedParams => {
    const formatterMap = {
      isDefaultLocation: value => (value === 'true' ? 'Yes' : 'No'),
      status: value => getActiveInactiveDeleteStatus(value),
      locationNumber: async value =>
        ServiceProviderLocationNumberSearchDisplay(
          value,
          pageId,
          dataProvider,
          snackbar,
        ),
    };

    return formListValueFormatter({ data: searchedParams, formatterMap });
  };

  const navigateLocationUrl = (recordId, subTab) => {
    return `/serviceproviders/edit/${pageId}/${currentIndex + 1}/${
      FORM_VIEW.EDIT
    }/${recordId}/${subTab}`;
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName: 'ServiceProviderLocationList',
    dataProvider,
    schema: listSchema,
    initialState: {
      showColumnFilters: false,
    },
    downloadApiUrl: null,
    listApiUrl,
    accessorFunctions: {
      id,
      serviceProviderId,
      createdByUserId,
      lastModifiedByUserId,
      locationName,
      locationNumber,
      isDefaultLocation,
      status,
    },
    accessorFunctionsAdditional: {
      navigate: recordId =>
        navigateLocationUrl(recordId, LOCATIONS_FORM_VIEW.DETAILS),
    },
    onRowCountChange: rowCount => {
      setResultsFound(rowCount);
    },
    sortOrder: 'ASC',
    sortBy: 'locationName',
  };

  const searchProps = {
    customComponents: FormFields,
    IconLibrary,
    schema: searchSchema,
    storeName: listProps.storeName,
    onSubmit: submitSearchForm,
    onClear: () => {
      store.domain.list.reset(listProps.storeName);
    },
    validationSchema: validationSchema(formatMessage),
    hideSearchForm: () => {
      setShowSearch(false);
    },
    initialValues: {
      serviceProviderId: pageId,
    },
    getValueByControl: getDefaultValueByControl,
  };

  const searchParamsProps = {
    storeName: listProps.storeName,
    preProcessSearchValues,
    schema: searchSchema,
    resultsFound,
  };

  useEffect(() => {
    const buttons = [];
    if (
      issueButton &&
      hasInvalidLocationAddress?.length > 0 &&
      action !== FORM_VIEW.EDIT
    ) {
      buttons.push(issueButton);
    }
    if (action === FORM_VIEW.CREATE) {
      buttons.push(listButton);
      store.domain.list.reset('ServiceProviderLocationList');
    } else if (action === FORM_VIEW.LIST) {
      buttons.push(createButton);
      buttons.push(searchButton);
    } else if (action === FORM_VIEW.EDIT) {
      buttons.push(createButton);
      buttons.push(listButton);
      store.domain.list.reset('ServiceProviderLocationList');
    }
    if (action !== FORM_VIEW.LIST) {
      setShowSearch(false);
    }

    setStackablePageButtons(buttons);
  }, [action, showSearch, tabErrors, pageId, entityId, currentIndex]);

  const onChangeRef = useRef(onChange).current;

  return (
    <PageSection
      HeaderContent={
        <StackablePageButtons
          backButton={false}
          buttons={stackablePageButtons}
        />
      }
      label={formatMessage(
        {
          id: 'SERVICE_PROVIDER_LOCATION_EDIT_HEADING',
          defaultMessage: 'Service Provider Location {locationName}',
        },
        {
          locationName: spLocationContext?.spLocation?.locationName
            ? ` - ${spLocationContext?.spLocation?.locationName}`
            : '',
        },
      )}>
      <Grid2 container direction="column" spacing={2}>
        <Grid2 xs={12}>
          <FormSearchResultAdvanced {...searchParamsProps} />
        </Grid2>

        {action === FORM_VIEW.CREATE && (
          <PageSection
            subSection
            containerProps={{ sx: { mb: 3 } }}
            label={formatMessage({
              id: 'SERVICEPROVIDERLOCATION_CREATE_NEW_HEADING',
              defaultMessage: 'Create New Location',
            })}>
            <ServiceProviderLocationCreate
              serviceProviderId={pageId}
              currentIndex={currentIndex}
            />
          </PageSection>
        )}

        {action === FORM_VIEW.EDIT && (
          <Box>
            <Box
              sx={{
                width: {
                  xs: '100%',
                  sm: '100%',
                },
              }}>
              <ArrowTabGroup
                value={tabValue}
                onChange={(event, newValue) => {
                  navigate(navigateLocationUrl(entityId, newValue));
                }}>
                <ArrowTab
                  value={LOCATIONS_FORM_VIEW.DETAILS}
                  arrowHeadArea={90}
                  maxTabs={4}
                  label="Location Details"
                />
                <ArrowTab
                  value={LOCATIONS_FORM_VIEW.SKILLS}
                  arrowBackArea={5}
                  maxTabs={5}
                  label="Location Skills"
                />
                <ArrowTab
                  value={LOCATIONS_FORM_VIEW.SETUP}
                  arrowBackArea={5}
                  maxTabs={5}
                  label="Setup"
                />
                {(userAccess.isAdmin() || userAccess.isSA()) && (
                  <ArrowTab
                    value={LOCATIONS_FORM_VIEW.SYSTEM_SETTINGS}
                    arrowBackArea={5}
                    maxTabs={5}
                    label="System Settings"
                  />
                )}
                <ArrowTab
                  value={LOCATIONS_FORM_VIEW.PERFORMANCE}
                  arrowBackArea={5}
                  maxTabs={5}
                  label="Performance"
                />
              </ArrowTabGroup>
            </Box>

            {parseInt(subTabNumber, 10) === LOCATIONS_FORM_VIEW.DETAILS && (
              <ServiceProviderLocationEdit
                serviceProviderId={pageId}
                locationId={entityId}
                onChange={onChangeRef}
                tabErrors={tabErrors}
              />
            )}
            {parseInt(subTabNumber, 10) === LOCATIONS_FORM_VIEW.SKILLS && (
              <ServiceProviderSkills
                onChange={onChangeRef}
                tabErrors={tabErrors}
              />
            )}
            {parseInt(subTabNumber, 10) ===
              LOCATIONS_FORM_VIEW.SYSTEM_SETTINGS && (
              <SystemSettings onChange={onChangeRef} action={action} />
            )}
            {parseInt(subTabNumber, 10) === LOCATIONS_FORM_VIEW.PERFORMANCE && (
              <Performance onChange={onChangeRef} action={action} />
            )}
            {parseInt(subTabNumber, 10) === LOCATIONS_FORM_VIEW.SETUP && (
              <Setup onChange={onChangeRef} action={action} />
            )}
          </Box>
        )}

        {action === FORM_VIEW.LIST && showSearch && (
          <PageSection
            subSection
            containerProps={{ sx: { mb: 3 } }}
            label={formatMessage({
              id: 'SERVICEPROVIDERLOCATION_SEARCH_HEADING',
              defaultMessage: 'Search Locations',
            })}>
            <Box>
              <CRUDComponent
                type={CRUD_COMPONENT_TYPE.SEARCH_ADVANCED}
                searchProps={searchProps}
              />
            </Box>
          </PageSection>
        )}

        {action === FORM_VIEW.LIST && (
          <PageSection
            subSection
            containerProps={{ sx: { mb: 3 } }}
            label={formatMessage({
              id: 'SERVICEPROVIDERLOCATION_SEARCH_HEADING',
              defaultMessage: 'Locations List',
            })}>
            <CRUDComponent
              type={CRUD_COMPONENT_TYPE.ADVANCED_LIST}
              listProps={listProps}
            />
          </PageSection>
        )}
      </Grid2>
    </PageSection>
  );
}

export default observer(ServiceProviderLocationList);
